import type { Transition, Easing, TargetAndTransition } from 'framer-motion';
import type { SlideshowCollageLayout, SlideshowConfig, SlideshowPhotoFit } from '../types';

/**
 * Presentation rules shared by the real venue display (SlideshowScreenPage)
 * and the settings preview (VenueDisplayPreview).
 *
 * These live in one place deliberately: a preview that drifts from what the TV
 * actually renders is worse than no preview at all.
 */

export const COLLAGE_SIZES: Record<SlideshowCollageLayout, number> = {
  '2-up': 2,
  '3-up': 3,
  '4-grid': 4,
  mosaic: 5,
};

export interface CellPlacement {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

/**
 * Mosaic runs on a 4×2 grid with one hero frame spanning 2×2. The hero can sit
 * left, right or centre; the arrangement is re-picked on a timer so the grid
 * itself keeps changing, not just the photos in it.
 */
export const MOSAIC_ARRANGEMENTS: CellPlacement[][] = [
  // Hero left
  [
    { col: 1, row: 1, colSpan: 2, rowSpan: 2 },
    { col: 3, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 4, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 3, row: 2, colSpan: 1, rowSpan: 1 },
    { col: 4, row: 2, colSpan: 1, rowSpan: 1 },
  ],
  // Hero right
  [
    { col: 3, row: 1, colSpan: 2, rowSpan: 2 },
    { col: 1, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 2, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 1, row: 2, colSpan: 1, rowSpan: 1 },
    { col: 2, row: 2, colSpan: 1, rowSpan: 1 },
  ],
  // Hero centre, smalls down the outer edges
  [
    { col: 2, row: 1, colSpan: 2, rowSpan: 2 },
    { col: 1, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 4, row: 1, colSpan: 1, rowSpan: 1 },
    { col: 1, row: 2, colSpan: 1, rowSpan: 1 },
    { col: 4, row: 2, colSpan: 1, rowSpan: 1 },
  ],
];

/** How often the mosaic re-arranges, as a multiple of the slide interval. */
export const MOSAIC_RESHUFFLE_MULTIPLIER = 4;

/**
 * In 'mixed' mode a collage slide holds for this multiple of the base
 * interval. A collage whose cells refresh independently needs longer than a
 * single photo to be worth looking at — at 1× you'd never see a cell change.
 */
export const MIXED_COLLAGE_DWELL_MULTIPLIER = 3;

/**
 * Each collage cell swaps on its own randomised schedule, drawn from this
 * fraction range of the base interval. Keeping the spread wide is what stops
 * the cells from visibly falling back into lockstep.
 */
export const CELL_DELAY_MIN_FACTOR = 0.7;
export const CELL_DELAY_MAX_FACTOR = 1.9;

const EASE_OUT = [0.25, 0.46, 0.45, 0.94] as unknown as Easing;

export interface SlideMotion {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
  transition: Transition;
}

/**
 * Layers are stacked (both mounted at once), so these are genuine
 * cross-transitions rather than an exit sequenced before an enter.
 */
export function getSlideMotion(name: string, direction: 1 | -1): SlideMotion {
  switch (name) {
    case 'slide':
      return {
        initial: { opacity: 0, x: direction * 100 + '%' },
        animate: { opacity: 1, x: '0%' },
        exit: { opacity: 0, x: direction * -100 + '%' },
        transition: { duration: 0.65, ease: EASE_OUT },
      };

    case 'zoom':
      return {
        initial: { opacity: 0, scale: 1.08 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.94 },
        transition: { duration: 0.8, ease: 'easeInOut' as Easing },
      };

    case 'fade-to-black':
      // Genuinely dips through black: the incoming layer waits for the
      // outgoing one to clear instead of fading in over it.
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.45, delay: 0.45, ease: 'easeIn' as Easing } },
        exit: { opacity: 0, transition: { duration: 0.45, ease: 'easeOut' as Easing } },
        transition: { duration: 0.45, ease: 'easeInOut' as Easing },
      };

    case 'kenburns':
      // The pan/zoom itself lives on the image; the layer only hands off.
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 1.1, ease: 'easeInOut' as Easing },
      };

    case 'crossfade':
    default:
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.9, ease: 'easeInOut' as Easing },
      };
  }
}

/**
 * Resolves the sizing actually applied to a photo. `preservePortrait`
 * overrides the configured fit for vertical photos only — filling or
 * stretching a portrait shot to a landscape screen either crops the subject
 * out or distorts it.
 */
export function effectiveFit(
  fit: SlideshowPhotoFit,
  isPortrait: boolean | null,
  preservePortrait: boolean,
): SlideshowPhotoFit {
  return preservePortrait && isPortrait ? 'fit' : fit;
}

/** Whether slide `slideNumber` renders as a collage under this config. */
export function isCollageSlideFor(
  config: Pick<SlideshowConfig, 'mode' | 'collageEveryN'>,
  slideNumber: number,
): boolean {
  if (config.mode === 'collage') return true;
  return (
    config.mode === 'mixed' &&
    config.collageEveryN > 0 &&
    slideNumber > 0 &&
    slideNumber % config.collageEveryN === 0
  );
}

/** Deterministic drift vector for Ken Burns, stable per photo. */
export function kenBurnsDrift(photoId: string): { x: number; y: number } {
  let hash = 0;
  for (let i = 0; i < photoId.length; i++) hash = (hash * 31 + photoId.charCodeAt(i)) | 0;
  const pick = (n: number) => ((Math.abs(hash >> n) % 100) / 100 - 0.5) * 4;
  return { x: pick(2), y: pick(6) };
}
